import ky, { HTTPError as KyHTTPError, Options as KyOptions } from "ky";
import snakeCaseKeys from "snakecase-keys";

import { camelizeKeys } from "./camelize";

import { endpoints as accountsEndpoints } from "./endpoints/accounts";
import { endpoints as mediaEndpoints } from "./endpoints/media";
import { endpoints as routesEndpoints } from "./endpoints/routes";
import { endpoints as venuesEndpoints } from "./endpoints/venues";
import { endpoints as profilesEndpoints } from "./endpoints/profiles";
import { endpoints as tagsEndpoints } from "./endpoints/tags";
import { endpoints as tasksEndpoints } from "./endpoints/tasks";
import { endpoints as competitionEndpoints } from "./endpoints/competitions";
import { endpoints as challengesEndpoints } from "./endpoints/challenges";
import { endpoints as creditsEndpoints } from "./endpoints/credits";
import { endpoints as questsEndpoints } from "./endpoints/quests";

import { AuthTokenResponse } from "./types/auth";
import { PaginatedResponse } from "./types";

export class HTTPError extends KyHTTPError {
  jsonData?: any;

  static async fromKyError(err: KyHTTPError) {
    const httpError = new HTTPError(err.response, err.request, err.options);
    try {
      httpError.jsonData = camelizeKeys(
        await err.response.json(),
        camelCaseExcludeRegex
      );
    } catch (err) {}
    return httpError;
  }
}

type APIConfig = {
  baseUrl: string;
  clientId?: string;
  clientSecret?: string;

  getToken?: (() => string) | (() => Promise<string>);
  setToken?: ((token: string) => void) | ((token: string) => Promise<void>);
  onAuthFailed?:
    | ((request: Request) => boolean)
    | ((request: Request) => Promise<boolean>);
};

const defaultConfig = {
  baseUrl: "https://api.griptonite.io",
};

const camelCaseExcludeRegex = [
  /\b([A-Z0-9_]+)\b|-1|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/,
];

type Options<R extends boolean = false> = KyOptions & {
  convertToCamelCase?: boolean;
  convertJsonBodyToSnakeCase?: boolean;
  returnResponse?: R;
};

type EndpointFunction = (client: ApiClient, ...args: any[]) => any;

type FetchResult<T, R extends boolean | undefined = undefined> = R extends true
  ? Response
  : T;

export default class ApiClient {
  ky: typeof ky;
  config: APIConfig;

  _attachToEndpoints = <T extends Record<string, EndpointFunction>>(
    endpoints: T
  ) => {
    return Object.fromEntries(
      Object.entries(endpoints).map(([key, endpoint]) => [
        key,
        (...args) => endpoint(this, ...args),
      ])
    ) as {
      // Remove the first arg from the function signature
      [K in keyof T]: T[K] extends (
        client: ApiClient,
        ...args: infer A
      ) => infer R
        ? (...args: A) => R
        : never;
    };
  };

  _addToken = async (request: Request, _: KyOptions) => {
    if (request.headers.has("Authorization")) {
      return;
    }
    const token = await this.getToken();
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
  };

  _handleAuthFailed = async (
    request: Request,
    _: unknown,
    response: Response
  ) => {
    if (response.status === 401) {
      if (this.config.onAuthFailed) {
        const retry = await this.config.onAuthFailed(request);
        if (retry) {
          return this.ky(request);
        }
      }
    }
  };

  constructor(config: APIConfig) {
    this.config = {
      ...defaultConfig,
      ...config,
    };
    this.ky = ky.create({
      prefixUrl: this.config.baseUrl,
      headers: {
        Accept: "application/json, text/plain, */*",
      },
      hooks: {
        beforeRequest: [this._addToken],
      },
      retry: {
        statusCodes: [408, 413, 429, 502, 503, 504],
      },
      timeout: 10000,
    });
  }

  configure(config: Partial<APIConfig>) {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  getToken = () => {
    return Promise.resolve(
      this.config.getToken ? this.config.getToken() : undefined
    );
  };

  setToken = (token: string) => {
    return Promise.resolve(
      this.config.setToken ? this.config.setToken(token) : undefined
    );
  };

  login = ({ email, password }: { email: string; password: string }) => {
    return this.post<AuthTokenResponse>("/auth/token", undefined, {
      body: new URLSearchParams({
        username: email,
        password,
        grant_type: "password",
      }),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization:
          "Basic " +
          window.btoa(`${this.config.clientId}:${this.config.clientSecret}`),
      },
    });
  };

  resetPassword = (email: string) => {
    return this.post(
      "/auth/reset-password",
      {
        email,
      },
      {
        headers: {
          Authorization:
            "Basic " +
            window.btoa(`${this.config.clientId}:${this.config.clientSecret}`),
        },
      }
    );
  };

  async fetch<T extends any = any, R extends boolean = false>(
    url: string,
    options?: Options<R>
  ): Promise<FetchResult<T, R>> {
    options = options ?? {};
    options.convertToCamelCase = options.convertToCamelCase ?? true;
    options.convertJsonBodyToSnakeCase =
      options.convertJsonBodyToSnakeCase ?? true;

    if (url.startsWith("/")) {
      url = url.slice(1);
    }
    if (options.convertJsonBodyToSnakeCase && options.json) {
      options.json = snakeCaseKeys(options.json as {}, {
        deep: true,
        exclude: camelCaseExcludeRegex,
      });
    }
    try {
      const response = await this.ky(url, {
        hooks: {
          afterResponse: [this._handleAuthFailed],
        },
        ...options,
      });
      if (options.returnResponse) {
        return response as FetchResult<T, R>;
      }
      if (response.status === 204) {
        return null as FetchResult<T, R>;
      }
      const data = await response.json<T>();
      // if is array or is object, camelcase keys
      if (
        options?.convertToCamelCase &&
        (Array.isArray(data) || typeof data === "object")
      ) {
        return camelizeKeys(data, camelCaseExcludeRegex) as FetchResult<T, R>;
      } else {
        return data as FetchResult<T, R>;
      }
    } catch (err) {
      // Attempt to parse the error response
      if (!(err instanceof KyHTTPError)) {
        throw err;
      }
      const httpError = await HTTPError.fromKyError(err);
      throw httpError;
    }
  }

  get<T extends any = any, R extends boolean = false>(
    url: string,
    options?: Options<R>
  ) {
    return this.fetch<T, R>(url, { method: "GET", ...options });
  }

  getAllPages = <T extends any = any, R extends false = false>(
    url: string,
    options?: Options<R>
  ) =>
    this.collectPages<T>(
      () => this.get<PaginatedResponse<T>, R>(url, options),
      options
    );

  collectPages = async <T extends any = any>(
    f: () => Promise<PaginatedResponse<T>>,
    options?: Options<false>
  ) => {
    let page = await f();
    let results = page.data;

    while (page.metadata.next) {
      page = await this.get(page.metadata.next, options);
      results = results.concat(page.data);
    }

    return results;
  };

  post<T extends any = any, R extends boolean = false>(
    url: string,
    json?: unknown,
    options?: Options<R>
  ) {
    return this.fetch<T, R>(url, {
      method: "POST",
      json,
      timeout: 30000,
      ...options,
    });
  }

  put<T extends any = any, R extends boolean = false>(
    url: string,
    json?: unknown,
    options?: Options<R>
  ) {
    return this.fetch<T, R>(url, {
      method: "PUT",
      json,
      timeout: 30000,
      ...options,
    });
  }

  delete<T extends null = null, R extends boolean = false>(
    url: string,
    options?: Options<R>
  ) {
    return this.fetch<T, R>(url, {
      method: "DELETE",
      timeout: 30000,
      ...options,
    });
  }

  patch<T extends any = any, R extends boolean = false>(
    url: string,
    json?: unknown,
    options?: Options<R>
  ) {
    return this.fetch<T, R>(url, {
      method: "PATCH",
      timeout: 30000,
      json,
      ...options,
    });
  }

  head<T extends any = any, R extends boolean = false>(
    url: string,
    options?: Options<R>
  ) {
    return this.fetch<T, R>(url, { method: "HEAD", ...options });
  }

  options<T extends any = any, R extends boolean = false>(
    url: string,
    options?: Options<R>
  ) {
    return this.fetch<T, R>(url, { method: "OPTIONS", ...options });
  }

  // Endpoint collections
  accounts = this._attachToEndpoints(accountsEndpoints);
  media = this._attachToEndpoints(mediaEndpoints);
  routes = this._attachToEndpoints(routesEndpoints);
  venues = this._attachToEndpoints(venuesEndpoints);
  profiles = this._attachToEndpoints(profilesEndpoints);
  tags = this._attachToEndpoints(tagsEndpoints);
  tasks = this._attachToEndpoints(tasksEndpoints);
  competitions = this._attachToEndpoints(competitionEndpoints);
  challenges = this._attachToEndpoints(challengesEndpoints);
  credits = this._attachToEndpoints(creditsEndpoints);
  quests = this._attachToEndpoints(questsEndpoints);
}
